import { css } from "lit";
//#region src/pd-generic-form/pd-generic-form.styles.ts
var pdGenericFormStyles = css`
  :host {
    display: block;
    container-type: inline-size;
  }

  :host([mode="edit"]) {
    display: flex;
    flex-direction: column;
    gap: var(--pd-form-container-row-gap, var(--pd-spacing-md));
  }

  /* View mode: two-column grid of stacked label/value rows separated by
   * hairlines (see docs mockup — layout, typography and spacing are
   * binding). Row width derives from the field type, never from the
   * concrete value. */
  .view-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: var(--pd-generic-form-view-column-gap, 36px);
  }

  .view-item {
    min-width: 0;
    padding: var(--pd-generic-form-view-row-padding, 12px) 0;
    border-bottom: 0.5px solid
      var(
        --pd-generic-form-view-separator-col,
        var(--pd-default-lightest-col, #e3eafa)
      );
  }

  .view-item--full {
    grid-column: 1 / -1;
  }

  .view-label {
    font-size: var(--pd-generic-form-view-label-size, 13px);
    font-weight: var(--pd-generic-form-view-label-weight, 400);
    color: var(
      --pd-generic-form-view-label-col,
      var(--pd-default-font-muted-col, #6b7280)
    );
    font-family: var(--pd-default-font-content-family);
    margin-bottom: 4px;
  }

  .view-value {
    min-width: 0;
    font-size: var(--pd-generic-form-view-value-size, 15px);
    font-weight: var(--pd-generic-form-view-value-weight, 500);
    line-height: 1.5;
    overflow-wrap: anywhere;
    color: var(
      --pd-generic-form-view-value-col,
      var(--pd-default-font-content-col, #333)
    );
    font-family: var(--pd-default-font-content-family);
  }

  /* Tri-state rows: the icon keeps its own flex column so multi-line
   * values flow under themselves, never under the icon. */
  .view-value--state {
    display: flex;
    gap: 7px;
  }

  /* Zustandsfarbe liegt AUSSCHLIESSLICH auf dem Glyph (check grün,
   * x rot, unknown muted) — nie auf Wert, Label, Hintergrund oder
   * Trennlinie. Hover-Farbe = Grundfarbe, das ist eine statische
   * Anzeige. */
  .state-icon {
    flex-shrink: 0;
    margin-top: 2px;
    padding: 0;
    --pd-icon-size: var(--pd-generic-form-view-state-icon-size, 16px);
  }

  .state-icon--yes {
    --pd-icon-col: var(
      --pd-generic-form-view-state-yes-col,
      var(--pd-default-success-col, #348b11)
    );
    --pd-icon-col-hover: var(
      --pd-generic-form-view-state-yes-col,
      var(--pd-default-success-col, #348b11)
    );
  }

  .state-icon--no {
    --pd-icon-col: var(
      --pd-generic-form-view-state-no-col,
      var(--pd-default-error-col, #a1141b)
    );
    --pd-icon-col-hover: var(
      --pd-generic-form-view-state-no-col,
      var(--pd-default-error-col, #a1141b)
    );
  }

  .state-icon--unknown {
    --pd-icon-col: var(
      --pd-generic-form-view-state-unknown-col,
      var(--pd-default-disabled-col, #9ca3af)
    );
    --pd-icon-col-hover: var(
      --pd-generic-form-view-state-unknown-col,
      var(--pd-default-disabled-col, #9ca3af)
    );
  }

  /* Unknown and missing read identically: NUR der Wert fällt eine
   * Helligkeitsstufe unter das Label-Grau und verliert sein Gewicht —
   * das Label behält seine Tonalität (Revision 2026-08-18 nach
   * Testfeedback: das frühere Ganzzeilen-Dämpfen ließ Feldname und
   * Inhalt zu einer grauen Fläche verschmelzen). Glyphfarbe kommt aus
   * den state-icon--*-Regeln. */
  .view-item--unknown .view-value {
    color: var(
      --pd-generic-form-view-unknown-col,
      var(--pd-default-disabled-col, #9ca3af)
    );
    font-weight: 400;
  }

  .view-value a {
    color: var(
      --pd-generic-form-view-link-col,
      var(--pd-default-font-link-col, inherit)
    );
    text-decoration: none;
  }

  .view-value a:hover {
    color: var(
      --pd-generic-form-view-link-hover-col,
      var(--pd-default-font-link-col-hover)
    );
  }

  .view-fieldset-legend {
    grid-column: 1 / -1;
    font-weight: var(--pd-generic-form-view-legend-weight, 600);
    font-size: var(
      --pd-generic-form-view-legend-size,
      var(--pd-default-font-content-size, 0.95rem)
    );
    color: var(
      --pd-generic-form-view-legend-col,
      var(--pd-default-font-content-col, #333)
    );
    font-family: var(--pd-default-font-content-family);
    padding: var(--pd-generic-form-view-legend-padding, 0.5rem 0 0.25rem);
  }

  .empty-state {
    color: var(--pd-default-font-muted-col, #6b7280);
    font-family: var(--pd-default-font-content-family);
  }

  /* Optionale Erklärung eines Feldes — leise, eine Zeile, in beiden Modi. */
  .with-description {
    display: flex;
    flex-direction: column;
  }

  .with-description > *:first-child {
    width: 100%;
  }

  .field-description {
    display: block;
    margin-top: var(--pd-spacing-xs, 0.25rem);
    font-size: 0.8em;
    color: var(--pd-default-font-muted-col, #6b7280);
    font-family: var(--pd-default-font-content-family);
  }

  /* Narrow containers (mobile / slim app boxes): single column — the
   * span-2 rows are naturally full-width then. One row part, two
   * layouts, one query. */
  @container (max-width: 480px) {
    .view-list {
      grid-template-columns: 1fr;
    }
  }
`;
//#endregion
export { pdGenericFormStyles };
